import { createContext, useContext, useEffect, useState } from 'react';
import { Member, Message } from '../types';
import { PublishResponse } from 'pubnub';
import { usePubNub } from 'pubnub-react';
import { useCallback } from 'react';
import { TrelloPowerUpContext } from '@powerupsclub/trello';

interface BoardContextProps {
  isLoading: boolean;
  isSending: boolean;
  lastMessageViewedId?: string;
  messages: Message[];
  me: Member | null;
  members: Map<string, Member>;
  error: Error | undefined | null;
  fetchMessages: () => Promise<void>;
  sendMessage: (text: string) => Promise<PublishResponse>;
  deleteMessage: (id: string) => Promise<boolean>;
  refetchLastMessageId: () => Promise<void>;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function mockMessage(text: string, index: number) {
  return {
    createdAt: new Date().toISOString(),
    timetoken: '0',
    id: `welcome-${index}`,
    idMember: 'bot',
    text,
    idBoard: '',
    member: {
      id: 'bot',
      fullName: 'Power-Ups Club',
      avatarUrl: '',
      avatar: '',
      initials: 'PC',
      username: 'powerups.club',
    },
  };
}

export const BoardContext = createContext<BoardContextProps>({
  isLoading: true,
  isSending: false,
  messages: [],
  members: new Map(),
  lastMessageViewedId: undefined,
  me: null,
  error: null,
  fetchMessages: () => {
    throw new Error('not implemented');
  },
  sendMessage: () => {
    throw new Error('not implemented');
  },
  deleteMessage: () => {
    throw new Error('not implemented');
  },
  refetchLastMessageId: () => {
    throw new Error('not implemented');
  },
});

export const BoardContextProvider: React.FC = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [lastMessageViewedId, setLastMessageViewedId] = useState<
    string | undefined
  >();
  const [me, setMe] = useState<Member | null>(null);
  const [members, setMembers] = useState<Map<string, Member>>(new Map());
  const [board, setBoard] = useState<any>(null);

  const [hasStartedPubNubSub, setHasStartedPubNubSub] = useState(false);

  const { isAuthorized, trelloIframe } = useContext(TrelloPowerUpContext);

  const [error, setError] = useState<Error | undefined | null>();

  const pubnub = usePubNub();

  const convertMessageEvent = (message: any): Message => {
    return {
      createdAt: new Date(message.timetoken / 1e4),
      timetoken: message.timetoken,
      id: `${message.uuid || message.publisher}:${message.timetoken}`,
      idBoard: message.channel,
      idMember: message.uuid || message.publisher,
      text: message.message,
      member: undefined,
      deleted: Boolean(message.actions?.deleted || false),
    };
  };

  const deleteMessage = useCallback(
    async (messageId) => {
      await pubnub.addMessageAction({
        channel: board.id,
        messageTimetoken: messageId.split(':')[1],
        action: {
          type: 'deleted',
          value: '.',
        },
      });

      return true;
    },
    [board, pubnub]
  );

  const onMessageHandler = useCallback((messageEvent: any) => {
    setMessages((messages) => [convertMessageEvent(messageEvent), ...messages]);
  }, []);

  const onMessageAction = useCallback((messageEvent: any) => {
    setMessages((messages) => {
      const messageIndex = messages.findIndex(
        (m) =>
          m.id ===
          `${messageEvent.data.uuid}:${messageEvent.data.messageTimetoken}`
      );

      const clonedMessages = [...messages];

      clonedMessages[messageIndex].deleted = true;

      return clonedMessages;
    });
  }, []);

  useEffect(() => {
    if (!board) {
      return;
    }

    if (hasStartedPubNubSub) {
      return;
    }

    pubnub.addListener({
      message: onMessageHandler,
      messageAction: onMessageAction,
    });

    pubnub.subscribe({ channels: [board.id] });
    setHasStartedPubNubSub(true);
  }, [pubnub, board, onMessageHandler, hasStartedPubNubSub, onMessageAction]);

  const fetchMessages = async () => {
    setError(null);
    setIsLoading(true);
    try {
      if (!me) {
        const _member = await trelloIframe.member('all');
        setMe(_member);
      }

      let _board = board;
      if (!board) {
        _board = await trelloIframe.board('all');
        setBoard(_board);

        const membersMap = new Map<string, Member>();

        for (const member of _board.members) {
          membersMap.set(member.id, member);
        }

        setMembers(membersMap);
      }

      const response = await pubnub.fetchMessages({
        channels: [_board.id],
        includeMessageActions: true,
      });

      const _messages = response.channels[_board.id].reverse();

      setMessages(_messages.map(convertMessageEvent));

      const _lastMessageViewedId = `${me?.id}:${_messages[0].timetoken}`;

      await trelloIframe.set(
        'board',
        'private',
        'lastMessageViewedId',
        _lastMessageViewedId
      );

      setLastMessageViewedId(_lastMessageViewedId);
    } catch (e) {
      setError(e as any);
    }
    setIsLoading(false);
  };

  const refetchLastMessageId = useCallback(async () => {
    const _lastMessageViewedId = await trelloIframe.get(
      'board',
      'private',
      'lastMessageViewedId'
    );
    setLastMessageViewedId(_lastMessageViewedId);
  }, [trelloIframe]);

  const sendMessage = async (message: string) => {
    setIsSending(true);
    const response = await pubnub.publish({ channel: board.id, message });

    const _lastMessageViewedId = `${me?.id}:${response.timetoken}`;

    trelloIframe.set(
      'board',
      'private',
      'lastMessageViewedId',
      _lastMessageViewedId
    );
    setLastMessageViewedId(_lastMessageViewedId);

    trelloIframe.set('board', 'shared', 'rerender', new Date().toISOString());

    setIsSending(false);
    return response;
  };

  const playWelcomeMessages = async () => {
    const _member = await trelloIframe.member('all');
    const _board = await trelloIframe.board('name');
    await sleep(1000);

    const fakeMessages = [
      mockMessage(
        `👋 Hi, ${_member.fullName}! Welcome to the "${_board.name}" board chatroom powered by the Board Chat Power-Up!`,
        0
      ),
    ];

    setMessages(fakeMessages);
    await sleep(1500);
    fakeMessages.unshift(
      mockMessage(
        `Click the "Authorize Board Chat" button below to authorize the Power-Up and start chatting with people on your board!`,
        1
      )
    );
    setMessages([...fakeMessages]);
  };

  useEffect(() => {
    if (isAuthorized) {
      fetchMessages();
    } else {
      playWelcomeMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  return (
    <BoardContext.Provider
      value={{
        me,
        members,
        isLoading,
        isSending,
        messages,
        lastMessageViewedId,
        error,
        fetchMessages,
        sendMessage,
        deleteMessage,
        refetchLastMessageId,
      }}
    >
      {children}
    </BoardContext.Provider>
  );
};
